import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  getWalletBalances,
  getWalletSources,
  earnToWalletA,
  spendFromWalletA,
  winToWalletB, 
  withdrawFromWalletB,
  convertWalletBToA,
  convertWalletAToB,
  initializeUserWallets
} from '../services/walletService';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, setDoc, increment } from 'firebase/firestore';
import { db } from '../services/firebase';

const WalletContext = createContext();

export const useWallet = () => {
  return useContext(WalletContext);
};

export const WalletProvider = ({ children }) => {
  const { currentUser, isDemoMode } = useAuth();
  const [wallets, setWallets] = useState({ walletA: 0, walletB: 0 });
  const [coinSources, setCoinSources] = useState({
    ads: 0,
    referrals: 0,
    login: 0,
    accuracyBonus: 0
  });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Update state from Firebase data
  const updateWalletState = (newWallets, newSources) => {
    if (!currentUser) return;
    
    // Validate and ensure numeric values
    const validatedWallets = {
      walletA: Number(newWallets?.walletA || 0),
      walletB: Number(newWallets?.walletB || 0)
    };
    
    const validatedSources = {
      ads: Number(newSources?.ads || 0),
      referrals: Number(newSources?.referrals || 0),
      login: Number(newSources?.login || 0),
      accuracyBonus: Number(newSources?.accuracyBonus || 0)
    };
    
    // Log wallet changes for debugging
    if (wallets.walletA !== validatedWallets.walletA) {
      console.log(`🔄 WALLET A CHANGED: ${wallets.walletA} → ${validatedWallets.walletA}`, {
        difference: validatedWallets.walletA - wallets.walletA,
        timestamp: new Date().toISOString()
      });
    }
    
    setWallets(validatedWallets);
    setCoinSources(validatedSources);
  };

  // Fetch wallet data from Firebase only
  const fetchWalletData = async () => {
    if (!currentUser) return null;
    
    try {
      console.log("Fetching wallet data from Firebase for user:", currentUser.uid);
      
      const balances = await getWalletBalances(currentUser.uid);
      const sources = await getWalletSources(currentUser.uid);
      
      console.log("Firebase wallet data:", { balances, sources });
      
      if (balances && sources) {
        return { balances, sources };
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching wallet data from Firebase:", error);
      return null;
    }
  };

  // Initialize wallet when component mounts
  useEffect(() => {
    const initWallet = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        console.log("Initializing wallet for user:", currentUser.uid);
        
        // Fetch from Firebase
        const walletData = await fetchWalletData();
        
        if (walletData) {
          updateWalletState(walletData.balances, walletData.sources);
        } else {
          // Initialize fresh wallet if no data exists
          console.log("No wallet data found, initializing fresh wallet");
          await initializeUserWallets(currentUser.uid);
          
          // Fetch the newly initialized wallet
          const newWalletData = await fetchWalletData();
          if (newWalletData) {
            updateWalletState(newWalletData.balances, newWalletData.sources);
          }
        }
        
        // Sync with referrals after initialization
        await syncWithReferrals();
        
        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing wallet:", error);
      } finally {
        setLoading(false);
      }
    };

    initWallet();
  }, [currentUser]);

  // Sync wallet with referral data
  const syncWithReferrals = async () => {
    if (!currentUser || isRefreshing) return;
    
    try {
      console.log('Syncing wallet with referrals data');
      
      // Get all users who used this referral code
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('referrerUID', '==', currentUser.uid));
      const referredUsersSnapshot = await getDocs(q);
      const referralCount = referredUsersSnapshot.size;
      
      if (referralCount === 0) return;
      
      console.log('Found', referralCount, 'referrals');
      
      // Calculate expected earnings
      const REFERRAL_REWARD = 1000;
      const expectedEarnings = referralCount * REFERRAL_REWARD;
      
      // Get current sources
      const sourcesRef = doc(db, 'users', currentUser.uid, 'wallets', 'sources');
      const sourcesDoc = await getDoc(sourcesRef);
      const currentReferralSource = sourcesDoc.exists() ? (sourcesDoc.data().referrals || 0) : 0;
      
      // Update if referral earnings don't match
      if (currentReferralSource !== expectedEarnings) {
        console.log('Fixing referral rewards from', currentReferralSource, 'to', expectedEarnings);
        
        const difference = expectedEarnings - currentReferralSource;
        
        // Update wallet balance
        const walletRef = doc(db, 'users', currentUser.uid, 'wallets', 'balances');
        await updateDoc(walletRef, {
          walletA: increment(difference)
        });
        
        // Update sources
        await updateDoc(sourcesRef, {
          referrals: expectedEarnings
        });
        
        // Update user document
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          totalReferralEarnings: expectedEarnings
        });
        
        // Refresh wallet state
        const updatedWalletData = await fetchWalletData();
        if (updatedWalletData) {
          updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
        }
        
        console.log('Referral rewards successfully updated');
      }
    } catch (error) {
      console.error('Error syncing wallet with referrals:', error);
    }
  };

  // Add coins to wallet A (from ads)
  const earnCoinsFromAds = async (amount) => {
    if (!currentUser) return false;
    
    try {
      await earnToWalletA(currentUser.uid, amount, 'ads');
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error earning coins from ads:", error);
      return false;
    }
  };

  // Add coins to wallet A (from blogs)
  const earnCoinsFromBlogs = async (amount) => {
    if (!currentUser) return false;
    
    try {
      await earnToWalletA(currentUser.uid, amount, 'ads');
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error earning coins from blogs:", error);
      return false;
    }
  };

  // Add coins to wallet A (from login bonus)
  const earnCoinsFromLogin = async (amount) => {
    if (!currentUser) return false;
    
    try {
      await earnToWalletA(currentUser.uid, amount, 'login');
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error earning coins from login:", error);
      return false;
    }
  };

  // Add coins to wallet A (from accuracy bonus)
  const earnAccuracyBonus = async (amount) => {
    if (!currentUser) return false;
    
    try {
      await earnToWalletA(currentUser.uid, amount, 'accuracyBonus');
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error earning accuracy bonus:", error);
      return false;
    }
  };

  // Legacy function for backwards compatibility
  const earnCoins = async (amount) => {
    return earnCoinsFromBlogs(amount);
  };

  // Spend coins from wallet A (for entry)
  const spendCoins = async (amount) => {
    if (!currentUser) return false;
    
    if (wallets.walletA < amount) {
      return false; // Not enough coins
    }
    
    try {
      console.log(`💰 SPENDING ${amount} coins from wallet A`);
      
      await spendFromWalletA(currentUser.uid, amount);
      
      // Fetch updated wallet data from Firebase to ensure accuracy
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
        console.log(`✅ Wallet updated after spending. New balance: ${updatedWalletData.balances.walletA}`);
      }
      
      return true;
    } catch (error) {
      console.error("Error spending coins:", error);
      return false;
    }
  };

  // Add winnings to wallet B
  const addWinnings = async (amount) => {
    if (!currentUser) return false;
    
    try {
      await winToWalletB(currentUser.uid, amount);
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error adding winnings:", error);
      return false;
    }
  };

  // Withdraw funds from wallet B
  const withdrawWinnings = async (amount) => {
    if (!currentUser) return false;
    
    if (wallets.walletB < amount) {
      return false; // Not enough funds in wallet B
    }
    
    try {
      await withdrawFromWalletB(currentUser.uid, amount);
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error withdrawing winnings:", error);
      return false;
    }
  };

  // Convert coins from wallet B to wallet A
  const convertToWalletA = async (amount) => {
    if (!currentUser) return false;
    
    if (wallets.walletB < amount) {
      return false; // Not enough funds in wallet B
    }
    
    try {
      await convertWalletBToA(currentUser.uid, amount);
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error converting to wallet A:", error);
      return false;
    }
  };

  // Convert coins from wallet A to wallet B
  const convertToWalletB = async (amount) => {
    if (!currentUser) return false;
    
    if (wallets.walletA < amount) {
      return false; // Not enough funds in wallet A
    }
    
    try {
      await convertWalletAToB(currentUser.uid, amount);
      
      // Fetch updated wallet data
      const updatedWalletData = await fetchWalletData();
      if (updatedWalletData) {
        updateWalletState(updatedWalletData.balances, updatedWalletData.sources);
      }
      
      return true;
    } catch (error) {
      console.error("Error converting to wallet B:", error);
      return false;
    }
  };

  // Get total balance
  const getTotalBalance = () => {
    return wallets.walletA + wallets.walletB;
  };

  // Get ad-earned coins available for conversion
  const getConvertibleCoins = () => {
    return coinSources.ads;
  };

  // Refresh wallet data from Firebase
  const refreshWallet = async () => {
    if (!currentUser) {
      console.log("No current user, can't refresh wallet");
      return { success: false, error: "Not logged in" };
    }
    
    if (isRefreshing) {
      console.log("Already refreshing wallet, skipping");
      return { success: false, error: "Already refreshing" };
    }
    
    try {
      setIsRefreshing(true);
      console.log("🔄 Refreshing wallet from Firebase");
      
      const walletData = await fetchWalletData();
      
      if (walletData) {
        updateWalletState(walletData.balances, walletData.sources);
        console.log("✅ Wallet refreshed successfully");
        return { success: true };
      } else {
        console.warn("Failed to refresh wallet data");
        return { success: false, error: "Failed to fetch wallet data" };
      }
    } catch (error) {
      console.error("Error refreshing wallet:", error);
      return { success: false, error: error.message };
    } finally {
      setIsRefreshing(false);
    }
  };

  const value = {
    wallets,
    coinSources,
    loading,
    isInitialized,
    earnCoinsFromBlogs,
    earnCoinsFromLogin,
    earnAccuracyBonus,
    earnCoins,
    spendCoins,
    addWinnings,
    withdrawWinnings,
    convertToWalletA,
    convertToWalletB,
    getConvertibleCoins,
    refreshWallet,
    getTotalBalance
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export default WalletContext;